/**
 * Which scenario an import's schedule changes go into.
 *
 * Pure, so the server's apply and the Import screen's hint line reach the same
 * verdict from the same list — the screen can say "creates a new draft" and
 * disable Apply on a refusal without guessing at a rule it would otherwise have
 * to restate.
 *
 * The target holds less than its name suggests. What an import writes to the
 * project header, its due date and its flags goes to the shared project tables
 * whichever scenario is named here, and a new project's steps are added
 * unassigned to every scenario, the live one included. The import reassigns no
 * one: the target is the draft it is recorded against, where the re-plan those
 * changes call for is done and published from.
 */

/** The scenario fields the decision reads. */
export interface ImportTargetCandidate {
  scenarioId: string;
  name: string;
  status: "draft" | "published" | "archived";
  /** A per-user working copy, emptied on the next exploration run. */
  isScratch?: boolean;
}

/**
 * Set only on the day's own name, which is exempt from the name clashes below:
 * which non-draft scenario shares it, so the hint can say so rather than let
 * "Day …" read as the live schedule.
 */
type SharedWith = { sharedWith?: "published" | "archived" };

export type ImportTarget =
  | ({ kind: "existing"; scenarioId: string; name: string } & SharedWith)
  | ({ kind: "create"; name: string } & SharedWith)
  | {
      kind: "refuse";
      /** `too_long` is a malformed request; every other reason is a conflict with what exists. */
      why: "too_long" | "live" | "archived" | "scratch" | "ambiguous";
      reason: string;
    };

/** Long enough for any real name, short enough to sit in the scenario bar. */
export const MAX_SCENARIO_NAME = 80;

/** The draft each day's changes accumulate into, named for the date. */
export const dayScenarioName = (day: string) => `Day ${day}`;

/**
 * How two names are compared: case, surrounding space and doubled space don't
 * count, and neither do a compatibility variant of a character (a full-width
 * letter, a ligature) or an invisible zero-width one pasted along with it —
 * each would otherwise let a name look identical to a refused one and pass.
 */
export const scenarioNameKey = (name: string) =>
  name
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
const key = scenarioNameKey;

export function chooseImportTarget(
  scenarios: readonly ImportTargetCandidate[],
  requestedName: string | null | undefined,
  defaultName: string,
): ImportTarget {
  const typed = (requestedName ?? "").trim();
  const name = typed || defaultName;

  if (name.length > MAX_SCENARIO_NAME) {
    return {
      kind: "refuse", why: "too_long",
      reason: `A scenario name can be at most ${MAX_SCENARIO_NAME} characters.`,
    };
  }

  // The day's own draft keeps the rule the rest of Today uses for it: an exact
  // name among drafts, the first if there are several, a fresh one if none. A
  // published or archived "Day …" is not a refusal here — publishing the
  // morning's draft is exactly how one gets there, and the next change that
  // day is meant to start a new draft under the same name, not be turned away.
  if (key(name) === key(defaultName)) {
    const day = scenarios.find((s) => s.name === defaultName && s.status === "draft");
    const clash = scenarios.filter((s) => s.status !== "draft" && key(s.name) === key(defaultName));
    const shared: SharedWith = clash.some((s) => s.status === "published")
      ? { sharedWith: "published" }
      : clash.length ? { sharedWith: "archived" } : {};
    return day
      ? { kind: "existing", scenarioId: day.scenarioId, name: day.name, ...shared }
      : { kind: "create", name: defaultName, ...shared };
  }

  const matches = scenarios.filter((s) => key(s.name) === key(name));

  // Checked before the drafts, so a draft that happens to share the live
  // schedule's name still refuses: "adds to X" must never be readable as
  // "adds to the live schedule".
  if (matches.some((s) => s.status === "published")) {
    return {
      kind: "refuse", why: "live",
      reason: `"${name}" is the live schedule — changes never go straight into it. Pick a draft or a new name.`,
    };
  }
  if (matches.some((s) => s.status === "archived")) {
    return {
      kind: "refuse", why: "archived",
      reason: `"${name}" is archived — changes never go into an archived scenario. Pick a draft or a new name.`,
    };
  }
  // A working copy is emptied by the next optimize or ask, so changes put
  // there would vanish without anyone choosing to discard them.
  if (matches.some((s) => s.isScratch)) {
    return {
      kind: "refuse", why: "scratch",
      reason: `"${name}" is a working copy that the next run overwrites. Pick a draft or a new name.`,
    };
  }
  if (matches.length > 1) {
    return {
      kind: "refuse", why: "ambiguous",
      reason: `More than one draft is named "${name}" — pick a unique name.`,
    };
  }
  const [only] = matches;
  if (only) return { kind: "existing", scenarioId: only.scenarioId, name: only.name };
  return { kind: "create", name };
}
